import {
  type FieldTypes,
  type Step,
  type StepGroup,
  type Field,
  type StepDetails,
  FieldType
} from "@certa/types";
import type { StepGroupsData } from "@certa/queries/types/workflow.types";
import type { Process } from "@certa/studio-core/src/types/process.types";
import { v3 as hash } from "murmurhash";
import { type FieldState } from "../store/fieldResponses.store";
import { omit, compact } from "lodash-es";
import { forwardParseDefaultValue } from "@certa/studio-parser";

/** Using murmurhash, as it gives same value for same input
 * And we need consistent id generation for a component between different renders */
export const getPreviewIdFromTag = (value: string) => {
  return hash(value);
};

export function transformMetaJSONToPlatformData(
  process: Process,
  fieldResponses: Record<string, FieldState>,
  currentUserGroups: string[] = []
): {
  stepGroupsData: StepGroupsData;
  stepsDetails: Record<string, StepDetails>;
  fieldIdMap: Record<number, string>;
  stepIdMap: Record<number, string>;
  swimlaneIdMap: Record<number, string>;
} {
  const stepGroups: StepGroup[] = [];
  const stepIdMap: Record<number, string> = {};
  const swimlaneIdMap: Record<number, string> = {};
  let fieldIdMap: Record<number, string> = {};

  const stepsDetails: Record<string, StepDetails> = {};

  process.process.swimlaneSequence
    ?.map(swimlaneId => process.swimlanes[swimlaneId])
    .forEach(swimlane => {
      const steps: Step[] = compact(
        swimlane.steps.map(stepUUID => {
          const stepDef = process.steps[stepUUID];
          if (!stepDef) return null;

          const stepId = getPreviewIdFromTag(stepDef.tag);
          stepIdMap[stepId] = stepDef.tag;
          return {
            id: stepId,
            name: stepDef.name,
            initiatedAt: null,
            completedAt: null,
            isCompleted: false, // This would need to come from runtime data
            overdue: false, // This would need to come from runtime data
            isLocked: false, // This would need to come from runtime data
            definitionTag: stepDef.tag,
            availableUserTags: stepDef.availableUserTags ? ["Assignee"] : [],
            firstAlertColor: "", // This would need to come from runtime data
            alerts: [], // This would need to come from runtime data
            deadline: null, // This would need to come from deadline configuration
            stepType: stepDef.stepType,
            isWizard: Boolean(stepDef.extra?.is_wizard),
            allUsers: [] // This would need to come from runtime data
          };
        })
      );

      const stepGroupId = getPreviewIdFromTag(swimlane.tag);
      const stepGroupDefinitionId = getPreviewIdFromTag(swimlane.tag);
      swimlaneIdMap[stepGroupId] = swimlane.tag;
      stepGroups.push({
        id: stepGroupId,
        name: swimlane.name,
        steps,
        definitionId: stepGroupDefinitionId,
        dynamicGroupNames: swimlane.dynamicGroupNames,
        overdue: false, // This would need to come from runtime data
        firstAlertColor: "", // This would need to come from runtime data
        alerts: [], // This would need to come from runtime data
        definitionTag: swimlane.tag,
        initiatedAt: null,
        completedAt: null,
        isCompleted: false, // This would need to come from runtime data
        accessibleBy: [], // This would need to be computed based on groups
        assignedTo: [] // This would need to come from runtime data
      });
    });

  Object.values(process.swimlanes).forEach(swimlane => {
    swimlane.steps.forEach(stepUUID => {
      const stepTag = process.steps[stepUUID].tag;
      const { stepDetail, fieldsInStepIdsMap } = transformMetaJSONToStepDetails(
        { process, stepUUID, stepTag, fieldResponses }
      );
      stepsDetails[getPreviewIdFromTag(stepTag)] = stepDetail;
      fieldIdMap = { ...fieldIdMap, ...fieldsInStepIdsMap };
    });
  });

  // Get tagged steps
  const taggedSteps = Object.values(process.steps)
    .filter(step => step.availableUserTags)
    .map(step => step.tag);

  // Get todo steps
  const todoSteps = Object.values(process.steps)
    .filter(step =>
      step.myTasksGroups?.some(group => currentUserGroups.includes(group))
    )
    .map(step => step.tag);

  // Get todo step groups
  const todoStepGroups = Object.values(process.swimlanes)
    .filter(swimlane =>
      swimlane.myTaskGroups?.some(group => currentUserGroups.includes(group))
    )
    .map(swimlane => swimlane.tag);

  // Filter step groups accessible by user
  const userStepGroups = stepGroups.filter(group => {
    const swimlane = Object.values(process.swimlanes).find(
      s => s.id === String(group.id)
    );
    return swimlane?.staticGroups?.some(group =>
      currentUserGroups.includes(group)
    );
  });

  return {
    stepGroupsData: {
      taggedSteps,
      defaultStepTags: process.process.defaultStepTags || [],
      defaultStepTagsForGroup: Object.values(
        process.process.defaultStepTagForGroups || {}
      ),
      todoStepGroups,
      todoSteps,
      progress: 0, // This would need to be calculated based on completed steps
      results: stepGroups,
      myStepGroups: [], // This would need runtime data to determine which groups belong to current user
      myTaggedSteps: [], // This would need runtime data to determine which steps are tagged to current user
      userStepGroups
    },
    stepsDetails,
    fieldIdMap,
    stepIdMap,
    swimlaneIdMap
  };
}

function transformMetaJSONToStepDetails(props: {
  process: Process;
  stepUUID: string;
  stepTag: string;
  fieldResponses: Record<string, FieldState>;
}): { stepDetail: StepDetails; fieldsInStepIdsMap: Record<number, string> } {
  const { process, stepUUID, stepTag, fieldResponses } = props;

  const fieldsInStepIdsMap: Record<number, string> = {};
  const stepDef = process.steps[stepUUID];
  if (!stepDef) {
    throw new Error(`Step definition not found for tag: ${stepUUID}`);
  }

  // Transform fields for this step
  const fields: Field[] = stepDef.fields.map(fieldUUID => {
    const fieldDef = process.fields[fieldUUID];

    if (!fieldDef) {
      throw new Error(`Field definition not found for id: ${fieldUUID}`);
    }
    const fieldId = getPreviewIdFromTag(fieldDef.tag);
    const fieldDefinitionId = getPreviewIdFromTag(fieldDef.tag); // Currently keeping it same as fieldId

    fieldsInStepIdsMap[fieldId] = fieldDef.tag;

    const fieldResponse = fieldResponses[fieldId];
    const isFieldTypeUnchanged = fieldResponse?.type === fieldDef.fieldType;
    const fieldAnswer: Field["answer"] =
      fieldResponse && isFieldTypeUnchanged
        ? omit(fieldResponse, "type")
        : null;

    return {
      id: fieldId,
      commentCount: 0, // Runtime data
      integrationCommentCounts: {}, // Runtime data
      searchParamData: fieldDef.searchParamJson || null,
      isRequired: fieldDef.isRequired || false,
      regexValue: fieldDef.regexValue || "",
      regexError: fieldDef.regexError || "",
      labelValue: fieldDef.body,
      createdAt: new Date(fieldDef.__createdAt).toISOString(),
      updatedAt: new Date(fieldDef.__modifiedAt).toISOString(),
      isEnabled: true,
      integrationJSON: {},
      extra: fieldDef.extra || {},
      alerts: [],
      statusIndicator: null,
      answer: fieldAnswer,
      definition: {
        id: fieldDefinitionId,
        // TODO: Needs some adjustment here, eg: integration
        // TODO: Why Platform is using deprecated FieldTypes?
        fieldType: fieldDef.fieldType as FieldTypes,
        body: fieldDef.body,
        tag: fieldDef.tag,
        helpText: fieldDef.helpText || "",
        isRequired: fieldDef.isRequired || false,
        regexValue: fieldDef.regexValue || "",
        regexError: fieldDef.regexError || "",
        size: fieldDef.size || 1,
        attachment: fieldDef.attachmentUrl || "",
        meta: {
          height: fieldDef?.meta?.height || undefined
        },
        defaultValue:
          forwardParseDefaultValue({
            defaultValue: fieldDef.defaultValue,
            fieldType: fieldDef.fieldType,
            envOrSysVariable: fieldDef.envOrSysVariable
          }) ?? "",
        disabled: fieldDef.disabled || false,
        hidden: fieldResponse ? fieldResponse.isHidden : !!fieldDef.hidden,
        extra: {
          ...fieldDef.extra,
          isIntegrationField: fieldDef.fieldType === FieldType.INTEGRATION
        },
        isEncrypted: fieldDef.isEncrypted || false,
        options: fieldDef.options || [],
        answer: fieldAnswer
      },
      commentFlags: [], // Runtime data
      isReadOnly: null // Runtime data
    };
  });

  return {
    stepDetail: {
      id: getPreviewIdFromTag(stepTag),
      commentCount: 0,
      isEditable: true,
      completedBy: null,
      completedAt: null,
      versions: [],
      approvedAt: null,
      createdAt: new Date(stepDef.__createdAt).toISOString(),
      updatedAt: new Date(stepDef.__modifiedAt).toISOString(),
      isLocked: false,
      fields,
      definition: {
        // TODO: Pass def id
        id: getPreviewIdFromTag(stepTag),
        dependentSteps: compact(
          stepDef?.dependentSteps?.map(dependentStepTag => {
            const dependentStep = Object.values(process.steps).find(
              step => step.tag === dependentStepTag
            )!;
            if (!dependentStep) return undefined;

            return {
              id: getPreviewIdFromTag(dependentStepTag),
              name: dependentStep.name,
              definitionTag: dependentStepTag
            };
          })
        ),
        name: stepDef.name,
        tag: stepDef.tag,
        availableUserTags: stepDef.availableUserTags ? ["Assignee"] : [],
        extra: stepDef.extra || {},
        stepType: stepDef.stepType || "doable",
        disableSubmitReason: undefined
      },
      canSubmitStep: true,
      canUndoStep: false,
      hasWriteAccess: true,
      isAppBuilderFrozen: false,
      isWorkflowBeingMigrated: false,
      isNonEditable: false
    },
    fieldsInStepIdsMap
  };
}
